import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Intersection observer for sticky behavior
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the announcement bar has been scrolled offscreen
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * Last recorded scroll position
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Timeout for hiding animation
   * @type {number | null}
   */
  #timeout = null;

  /**
   * Animation delay duration
   * @constant {number}
   */
  #animationDelay = 150;

  connectedCallback() {
    super.connectedCallback();

    // Initialize slides without animation on first load
    this.refs.slides?.forEach((slide, index) => {
      if (index === 0) {
        slide.style.transform = 'translateX(0)';
        slide.style.opacity = '1';
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.style.transform = 'translateX(100%)';
        slide.style.opacity = '0';
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Initialize sticky behavior
    this.#initializeSticky();

    this.play();
  }

  /**
   * Initialize sticky behavior based on the sticky attribute
   */
  #initializeSticky() {
    const announcementBar = document.getElementById('announcement-bar-component');
    if (!announcementBar) {
      console.log('[Announcement Bar Debug] No announcement bar element found');
      return;
    }

    const stickyMode = announcementBar.getAttribute('sticky');
    console.log('[Announcement Bar Debug] Sticky mode:', stickyMode);
    console.log('[Announcement Bar Debug] Parent section:', announcementBar.closest('.shopify-section-group-header-group'));

    // Check computed styles
    const parentSection = announcementBar.closest('.shopify-section-group-header-group');
    if (parentSection) {
      const computedStyles = window.getComputedStyle(parentSection);
      console.log('[Announcement Bar Debug] Parent section styles:', {
        position: computedStyles.position,
        top: computedStyles.top,
        zIndex: computedStyles.zIndex
      });
    }

    // Check announcement bar styles
    const barStyles = window.getComputedStyle(announcementBar);
    console.log('[Announcement Bar Debug] Announcement bar styles:', {
      position: barStyles.position,
      top: barStyles.top,
      zIndex: barStyles.zIndex
    });

    if (stickyMode === 'always') {
      console.log('[Announcement Bar Debug] Initializing "always" sticky mode');
      this.#observeStickyPosition(true);
    } else if (stickyMode === 'scroll-up') {
      console.log('[Announcement Bar Debug] Initializing "scroll-up" sticky mode');
      this.#observeStickyPosition(false);
      this.#setupScrollListener();
    }
  }

  /**
   * Observes the announcement bar position for sticky behavior
   * @param {Boolean} alwaysSticky - Determines if bar is always sticky
   */
  #observeStickyPosition(alwaysSticky = true) {
    if (this.#intersectionObserver) return;

    const announcementBar = document.getElementById('announcement-bar-component');
    if (!announcementBar) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    console.log('[Announcement Bar Debug] Setting up intersection observer with config:', config);

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;
      console.log('[Announcement Bar Debug] Intersection observed:', {
        isIntersecting,
        boundingClientRect: entry.boundingClientRect,
        intersectionRatio: entry.intersectionRatio
      });

      if (alwaysSticky) {
        const newState = isIntersecting ? 'inactive' : 'active';
        console.log('[Announcement Bar Debug] Setting sticky state to:', newState);
        announcementBar.dataset.stickyState = newState;
      } else {
        this.#offscreen = !isIntersecting || announcementBar.dataset.stickyState === 'active';
        console.log('[Announcement Bar Debug] Offscreen status:', this.#offscreen);
      }
    }, config);

    this.#intersectionObserver.observe(announcementBar);
  }

  /**
   * Set up scroll listener for scroll-up sticky behavior
   */
  #setupScrollListener() {
    const announcementBar = document.getElementById('announcement-bar-component');
    if (!announcementBar) return;

    console.log('[Announcement Bar Debug] Setting up scroll listener');

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollDirection = scrollTop > this.#lastScrollTop ? 'down' : 'up';

      announcementBar.dataset.scrollDirection = scrollDirection;

      if (scrollDirection === 'up' && this.#offscreen) {
        console.log('[Announcement Bar Debug] Scrolling up while offscreen, showing announcement');
        clearTimeout(this.#timeout);
        announcementBar.dataset.stickyState = 'active';
        announcementBar.dataset.animating = 'true';

        this.#timeout = setTimeout(() => {
          delete announcementBar.dataset.animating;
        }, this.#animationDelay);
      } else if (scrollDirection === 'down' && announcementBar.dataset.stickyState === 'active') {
        console.log('[Announcement Bar Debug] Scrolling down while active, hiding announcement');
        clearTimeout(this.#timeout);
        announcementBar.dataset.stickyState = 'idle';
      }

      this.#lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    }, { passive: true });
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const previousIndex = this.#current;
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    let previousRelativeIndex = previousIndex % (this.refs.slides ?? []).length;
    if (previousRelativeIndex < 0) {
      previousRelativeIndex += (this.refs.slides ?? []).length;
    }

    // First, prepare the next slide off-screen
    this.refs.slides?.forEach((slide, index) => {
      if (index === relativeIndex && index !== previousRelativeIndex) {
        // Prepare incoming slide
        slide.style.transform = 'translateX(100%)';
        slide.style.opacity = '0';
      }
    });

    // Force reflow to ensure the transform is applied
    void this.offsetHeight;

    // Then animate the transition
    this.refs.slides?.forEach((slide, index) => {
      const isActive = index === relativeIndex;
      const wasActive = index === previousRelativeIndex;

      slide.setAttribute('aria-hidden', `${!isActive}`);

      if (isActive) {
        // Animate incoming slide
        slide.style.transform = 'translateX(0)';
        slide.style.opacity = '1';
      } else if (wasActive) {
        // Animate outgoing slide
        slide.style.transform = 'translateX(-100%)';
        slide.style.opacity = '0';
      }
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
